import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

/** Select the launch location once, after its owner loads. Later user choices win. */
export function useLocationPrefill<T extends { id?: string }>({
  locationId,
  ownerId,
  contactId,
  addresses,
  enabled,
  onSelect,
  onUnavailable,
  addressesReady = true,
}: {
  locationId?: string;
  ownerId?: string;
  contactId: string | null;
  addresses: T[];
  enabled: boolean;
  onSelect: (index: number, address: T) => void;
  onUnavailable: () => void;
  addressesReady?: boolean;
}) {
  const applied = useRef(false);
  useEffect(() => {
    if (!enabled || applied.current || !locationId || !ownerId) return;
    // A different customer chosen while loading cancels the launch prefill.
    if (contactId && contactId !== ownerId) {
      applied.current = true;
      return;
    }
    if (contactId !== ownerId || !addressesReady) return;
    const index = addresses.findIndex((address) => address.id === locationId);
    applied.current = true;
    if (index < 0) {
      onUnavailable();
      toast.error(
        'This location is no longer available for this customer. Select a service address.',
      );
      return;
    }
    onSelect(index, addresses[index]);
  }, [enabled, locationId, ownerId, contactId, addresses, onSelect, onUnavailable, addressesReady]);
}
